import React from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, X, Zap, AlertTriangle } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';

/* ====== BILLING VIEW IMPORTS - COMMENTED OUT (now redirects to /dashboard/settings?section=billing) ======
import { useState, useRef, useCallback, useEffect } from 'react';
import { ArrowLeft, CheckCircle, Loader2, XCircle, Minus, Plus, Crown } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useApi } from '../../hooks/useApi';
import { toast } from 'react-hot-toast';

const plans = [
  {
    id: 'standard',
    name: 'Standard',
    price: 3,
    icon: 'zap',
    features: [
      'All features',
      'Unlimited team members',
      'Advanced analytics'
    ]
  },
  {
    id: 'premium',
    name: 'Premium',
    price: 7,
    icon: 'crown',
    features: [
      'All features',
      'Org Intelligence',
      'Priority support',
      'Advanced AI'
    ]
  }
];
====== END BILLING VIEW IMPORTS COMMENT ====== */

const AILimitModal = ({ isOpen, onClose }) => {
  const { mode } = useTheme();
  const navigate = useNavigate();
  const isDark = mode === 'dark';

  /* ====== BILLING VIEW STATE - COMMENTED OUT ======
  const { user } = useAuth();
  const api = useApi();
  const [view, setView] = useState('limit');
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [memberCount, setMemberCount] = useState(1);
  const [currentSlots, setCurrentSlots] = useState(1);
  const [couponCode, setCouponCode] = useState('');
  const [actualCouponId, setActualCouponId] = useState(null);
  const [couponValid, setCouponValid] = useState(null);
  const [couponVerifying, setCouponVerifying] = useState(false);
  const [couponDiscount, setCouponDiscount] = useState(null);
  const couponTimeoutRef = useRef(null);

  // Fetch current org slot usage when billing view opens
  useEffect(() => {
    if (view === 'billing') {
      const fetchSlots = async () => {
        try {
          const response = await api.get('/api/v1/billing/profile');
          if (response.data.success) {
            const data = response.data.data;
            const used = data.usedMemberSlots || 1;
            setCurrentSlots(used);
            const minMembers = Math.max(1, used - 1);
            setMemberCount(minMembers);
          }
        } catch (err) {
          // fallback defaults
        }
      };
      fetchSlots();
    }
  }, [view, api]);

  const minMemberCount = Math.max(1, currentSlots - 1);
  const totalSeats = memberCount + 1; // +1 for admin
  ====== END BILLING VIEW STATE COMMENT ====== */

  const handleClose = () => {
    onClose();
  };

  /* ====== BILLING VIEW FUNCTIONS - COMMENTED OUT ======
  const handleCloseWithReset = () => {
    onClose();
    setTimeout(() => {
      setView('limit');
      setSelectedPlan(null);
      setMemberCount(1);
      setCouponCode('');
      setActualCouponId(null);
      setCouponValid(null);
      setCouponDiscount(null);
    }, 300);
  };

  const verifyCouponCode = useCallback(async (code) => {
    if (!code || code.trim() === '') {
      setActualCouponId(null);
      setCouponValid(null);
      setCouponDiscount(null);
      return;
    }

    setCouponVerifying(true);
    try {
      const response = await api.post('/api/v1/public/billing/verify-coupon', {
        couponCode: code.trim(),
        memberCount: totalSeats,
      });

      if (response.data.success && response.data.data.valid) {
        setCouponValid(true);
        setCouponDiscount(response.data.data.discount);
        setActualCouponId(response.data.data.couponCode);
        toast.success(`Coupon applied! ${response.data.data.discount}`);
      } else {
        setCouponValid(false);
        setCouponDiscount(null);
        setActualCouponId(null);
        toast.error(response.data.message || 'Invalid coupon code');
      }
    } catch (error) {
      setCouponValid(false);
      setCouponDiscount(null);
      setActualCouponId(null);
      toast.error(error.response?.data?.message || 'Failed to verify coupon');
    } finally {
      setCouponVerifying(false);
    }
  }, [api, totalSeats]);

  const handleCouponChange = (e) => {
    const value = e.target.value.toUpperCase();
    setCouponCode(value);
    setCouponValid(null);
    setCouponDiscount(null);
    setActualCouponId(null);

    if (couponTimeoutRef.current) {
      clearTimeout(couponTimeoutRef.current);
    }

    if (value.trim()) {
      couponTimeoutRef.current = setTimeout(() => {
        verifyCouponCode(value);
      }, 800);
    }
  };

  const handleCouponBlur = () => {
    if (couponTimeoutRef.current) {
      clearTimeout(couponTimeoutRef.current);
    }
    if (couponCode.trim() && couponValid === null) {
      verifyCouponCode(couponCode);
    }
  };

  const handleClearCoupon = () => {
    if (couponTimeoutRef.current) {
      clearTimeout(couponTimeoutRef.current);
    }
    setCouponCode('');
    setActualCouponId(null);
    setCouponValid(null);
    setCouponDiscount(null);
  };

  const handleSubscribe = async () => {
    if (!selectedPlan) {
      toast.error('Please select a plan');
      return;
    }

    setLoading(true);
    try {
      toast.loading(`Upgrading to ${selectedPlan.name}...`, { id: 'upgrade-loading' });

      const response = await api.post('/api/v1/public/billing/organization-subscription-checkout', {
        userId: user?.id,
        plan: selectedPlan.id,
        memberCount: memberCount,
        ...(actualCouponId && { couponCode: actualCouponId })
      });

      if (response.data.success) {
        toast.dismiss('upgrade-loading');
        window.location.href = response.data.data.url;
      } else {
        toast.dismiss('upgrade-loading');
        toast.error(response.data.message || 'Failed to start upgrade');
      }
    } catch (error) {
      toast.dismiss('upgrade-loading');
      toast.error(error.response?.data?.message || 'Failed to upgrade plan');
    } finally {
      setLoading(false);
    }
  };
  ====== END BILLING VIEW FUNCTIONS COMMENT ====== */

  const renderLimitView = () => (
    <motion.div
      key="limit"
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      transition={{ duration: 0.2 }}
    >
      {/* Icon with glow effect */}
      <div className="flex justify-center mb-5">
        <div className="relative">
          <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-purple-500 to-indigo-600 blur-xl opacity-40" />
          <div className="relative w-14 h-14 rounded-2xl bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center shadow-lg">
            <Sparkles size={26} className="!text-white" />
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="text-center mb-6">
        <h2 className={`text-lg font-semibold mb-1.5 ${isDark ? '!text-white' : 'text-gray-900'}`}>
          AI Limit Reached
        </h2>
        <p className={`text-[13px] leading-relaxed ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
          You've used all your free AI requests on the Free Trial plan.
        </p>
      </div>

      {/* Usage indicator */}
      <div className={`rounded-xl p-4 mb-6 ${
        isDark ? 'bg-white/5 border border-white/10' : 'bg-gray-50 border border-gray-100'
      }`}>
        <div className="flex items-center justify-between mb-2.5">
          <span className={`text-xs font-medium ${isDark ? 'text-white/60' : 'text-gray-500'}`}>
            AI Requests Used
          </span>
          <span className={`text-xs font-semibold ${isDark ? 'text-red-400' : 'text-red-500'}`}>
            10 / 10
          </span>
        </div>
        <div className={`w-full h-2 rounded-full overflow-hidden ${
          isDark ? 'bg-white/10' : 'bg-gray-200'
        }`}>
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: '100%' }}
            transition={{ duration: 0.8, delay: 0.2, ease: 'easeOut' }}
            className="h-full rounded-full bg-gradient-to-r from-purple-500 to-indigo-500"
          />
        </div>
        <div className={`flex items-center gap-1.5 mt-2.5 ${isDark ? 'text-white/40' : 'text-gray-400'}`}>
          <AlertTriangle size={12} />
          <span className="text-[11px]">Limit resets with plan upgrade</span>
        </div>
      </div>

      {/* Upgrade message */}
      <div className={`rounded-xl p-4 mb-6 ${
        isDark ? 'bg-gradient-to-r from-purple-500/10 to-indigo-500/10 border border-purple-500/20' : 'bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-100'
      }`}>
        <p className={`text-[13px] leading-relaxed text-center ${isDark ? 'text-white/70' : 'text-gray-700'}`}>
          To continue using AI features, upgrade to a <span className={`font-semibold ${isDark ? 'text-purple-300' : 'text-purple-600'}`}>paid plan</span>
        </p>
      </div>

      {/* Upgrade button - redirects to billing settings */}
      <motion.button
        whileHover={{ scale: 1.01 }}
        whileTap={{ scale: 0.98 }}
        onClick={() => {
          handleClose();
          navigate('/dashboard/settings?section=billing');
        }}
        className="w-full flex items-center justify-center gap-2 py-3 px-6 rounded-xl font-semibold text-sm !text-white bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 transition-all duration-200 shadow-lg shadow-purple-500/25 hover:shadow-purple-500/40"
      >
        <Zap size={16} />
        Upgrade
      </motion.button>

      {/* Dismiss link */}
      <button
        onClick={handleClose}
        className={`w-full text-center mt-3 py-1.5 text-[13px] rounded-lg transition-all duration-150 ${
          isDark ? 'text-white/30 hover:text-white/60 hover:bg-white/5' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-50'
        }`}
      >
        Maybe later
      </button>
    </motion.div>
  );

  /* ====== BILLING VIEW RENDER - COMMENTED OUT ======
  const renderBillingView = () => {
    const monthlyPrice = selectedPlan ? totalSeats * selectedPlan.price : 0;

    return (
      <motion.div
        key="billing"
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: 20 }}
        transition={{ duration: 0.2 }}
      >
        <div className="flex items-center gap-3 mb-5">
          <button
            onClick={() => setView('limit')}
            className={`p-1.5 rounded-lg transition-all duration-150 ${
              isDark ? 'text-white/50 hover:text-white/80 hover:bg-white/10' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
            }`}
          >
            <ArrowLeft size={16} />
          </button>
          <h2 className={`text-lg font-semibold ${isDark ? '!text-white' : 'text-gray-900'}`}>
            Choose Your Plan
          </h2>
        </div>

        <div className="grid grid-cols-2 gap-2.5 mb-4">
          {plans.map((plan) => {
            const isSelected = selectedPlan?.id === plan.id;
            return (
              <button
                key={plan.id}
                onClick={() => setSelectedPlan(plan)}
                className={`text-left rounded-xl p-3 border-2 transition-all duration-150 ${
                  isSelected
                    ? isDark
                      ? 'border-purple-500 bg-purple-500/10'
                      : 'border-purple-500 bg-purple-50'
                    : isDark
                      ? 'border-white/10 bg-white/5 hover:border-white/20'
                      : 'border-gray-200 bg-gray-50 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center gap-2 mb-1.5">
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 border ${
                    isDark ? 'bg-gradient-to-br from-purple-500/20 to-purple-600/15 border-purple-400/25' : 'bg-purple-100 border-purple-300'
                  }`}>
                    {plan.icon === 'crown' ? (
                      <Crown size={13} className={isDark ? 'text-purple-300' : 'text-purple-600'} />
                    ) : (
                      <Zap size={13} className={isDark ? 'text-blue-400' : 'text-blue-600'} />
                    )}
                  </div>
                  <span className={`text-[11px] font-semibold leading-tight ${isDark ? '!text-white' : 'text-gray-900'}`}>
                    {plan.name}
                  </span>
                </div>

                <div className="flex items-baseline gap-0.5 mb-2">
                  <span className={`text-lg font-bold ${isDark ? '!text-white' : 'text-gray-900'}`}>
                    ${plan.price}
                  </span>
                  <span className={`text-[9px] ${isDark ? 'text-white/40' : 'text-gray-400'}`}>
                    /mo per slot
                  </span>
                </div>

                <ul className="space-y-1">
                  {plan.features.map((feature) => (
                    <li key={feature} className="flex items-center gap-1.5">
                      <CheckCircle size={10} className="text-green-500 flex-shrink-0" />
                      <span className={`text-[10px] ${isDark ? 'text-white/60' : 'text-gray-600'}`}>{feature}</span>
                    </li>
                  ))}
                </ul>
              </button>
            );
          })}
        </div>

        <AnimatePresence>
          {selectedPlan && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="overflow-hidden"
            >
              <div className={`rounded-xl p-3 mb-4 ${
                isDark ? 'bg-white/5 border border-white/10' : 'bg-gray-50 border border-gray-200'
              }`}>
                <label className={`text-[11px] font-semibold block text-center mb-2 ${isDark ? '!text-white' : 'text-gray-900'}`}>
                  Number of Team Members
                </label>
                <div className="flex items-center justify-center gap-2.5 mb-2">
                  <button
                    onClick={() => setMemberCount(Math.max(minMemberCount, memberCount - 1))}
                    disabled={memberCount <= minMemberCount}
                    className={`w-7 h-7 rounded-lg flex items-center justify-center transition-all ${
                      memberCount <= minMemberCount
                        ? isDark ? 'bg-white/5 text-white/20 cursor-not-allowed' : 'bg-gray-100 text-gray-300 cursor-not-allowed'
                        : isDark ? 'bg-white/10 text-white/70 hover:bg-white/20' : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                    }`}
                  >
                    <Minus size={14} />
                  </button>
                  <input
                    type="number"
                    value={memberCount}
                    onChange={(e) => {
                      const val = parseInt(e.target.value) || minMemberCount;
                      setMemberCount(Math.max(minMemberCount, Math.min(1000, val)));
                    }}
                    min={minMemberCount}
                    max={1000}
                    className={`w-14 text-center py-1 rounded-lg text-sm font-semibold outline-none ${
                      isDark
                        ? 'bg-white/10 border border-white/20 text-white focus:border-purple-500/50'
                        : 'bg-white border border-gray-300 text-gray-900 focus:border-purple-500'
                    }`}
                  />
                  <button
                    onClick={() => setMemberCount(Math.min(1000, memberCount + 1))}
                    className={`w-7 h-7 rounded-lg flex items-center justify-center transition-all ${
                      isDark ? 'bg-white/10 text-white/70 hover:bg-white/20' : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                    }`}
                  >
                    <Plus size={14} />
                  </button>
                </div>

                <div className="text-center space-y-0.5 mb-2">
                  <p className={`text-[11px] ${isDark ? 'text-white/40' : 'text-gray-500'}`}>
                    Currently using: {currentSlots} slots (1 admin + {currentSlots - 1} members)
                  </p>
                  <p className={`text-[11px] ${isDark ? 'text-white/40' : 'text-gray-500'}`}>
                    Total seats: {totalSeats} (1 admin + {memberCount} members)
                  </p>
                </div>

                <div className={`border-t pt-2 text-center ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
                  <p className={`text-[10px] ${isDark ? 'text-white/40' : 'text-gray-500'}`}>Monthly Total</p>
                  <p className={`text-lg font-bold ${isDark ? '!text-white' : 'text-gray-900'}`}>
                    ${monthlyPrice.toFixed(2)}<span className={`text-[10px] font-normal ${isDark ? 'text-white/40' : 'text-gray-400'}`}>/month</span>
                  </p>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="mb-4">
          <label className={`text-[11px] font-medium mb-1.5 block ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
            Coupon Code (optional)
          </label>
          <div className="relative">
            <input
              type="text"
              value={couponCode}
              onChange={handleCouponChange}
              onBlur={handleCouponBlur}
              placeholder="Enter coupon code"
              className={`w-full px-4 py-2.5 pr-10 rounded-xl text-xs font-medium uppercase tracking-wide transition-all duration-150 outline-none ${
                couponValid === true
                  ? isDark
                    ? 'bg-green-500/10 border border-green-500/30 text-green-400 placeholder-green-500/30'
                    : 'bg-green-50 border border-green-300 text-green-700 placeholder-green-300'
                  : couponValid === false
                    ? isDark
                      ? 'bg-red-500/10 border border-red-500/30 text-red-400 placeholder-red-500/30'
                      : 'bg-red-50 border border-red-300 text-red-700 placeholder-red-300'
                    : isDark
                      ? 'bg-white/5 border border-white/10 text-white placeholder-white/30 focus:border-purple-500/50'
                      : 'bg-gray-50 border border-gray-200 text-gray-900 placeholder-gray-400 focus:border-purple-500'
              }`}
            />
            <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center">
              {couponVerifying ? (
                <Loader2 size={14} className={`animate-spin ${isDark ? 'text-white/40' : 'text-gray-400'}`} />
              ) : couponValid === true ? (
                <CheckCircle size={14} className="text-green-500" />
              ) : couponValid === false ? (
                <button onClick={handleClearCoupon} className="p-0.5">
                  <XCircle size={14} className="text-red-500 hover:text-red-400 transition-colors" />
                </button>
              ) : couponCode.trim() ? (
                <button onClick={handleClearCoupon} className="p-0.5">
                  <X size={14} className={`${isDark ? 'text-white/30 hover:text-white/60' : 'text-gray-400 hover:text-gray-600'} transition-colors`} />
                </button>
              ) : null}
            </div>
          </div>
          {couponValid === true && couponDiscount && (
            <p className="text-[11px] mt-1.5 text-green-500 font-medium">
              {couponDiscount}
            </p>
          )}
          {couponValid === false && (
            <p className={`text-[11px] mt-1.5 ${isDark ? 'text-red-400' : 'text-red-500'}`}>
              Invalid coupon code
            </p>
          )}
        </div>

        <motion.button
          whileHover={{ scale: 1.01 }}
          whileTap={{ scale: 0.98 }}
          onClick={handleSubscribe}
          disabled={!selectedPlan || loading}
          className={`w-full flex items-center justify-center gap-2 py-3 px-6 rounded-xl font-semibold text-sm !text-white transition-all duration-200 ${
            selectedPlan && !loading
              ? 'bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 shadow-lg shadow-purple-500/25 hover:shadow-purple-500/40'
              : isDark ? 'bg-white/10 cursor-not-allowed' : 'bg-gray-300 cursor-not-allowed'
          }`}
        >
          {loading ? (
            <>
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              Processing...
            </>
          ) : (
            <>
              <Sparkles size={16} />
              {selectedPlan ? `Upgrade to ${selectedPlan.name}` : 'Select a plan'}
            </>
          )}
        </motion.button>
      </motion.div>
    );
  };
  ====== END BILLING VIEW RENDER COMMENT ====== */

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backgroundColor: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(8px)' }}
          onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 24 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 24 }}
            transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
            className={`relative w-full max-w-[420px] max-h-[90vh] rounded-2xl shadow-2xl overflow-hidden flex flex-col ${
              isDark ? 'bg-gray-900 border border-white/10' : 'bg-white border border-gray-100'
            }`}
          >
            {/* Top gradient accent bar */}
            <div className="h-1 w-full bg-gradient-to-r from-purple-500 via-indigo-500 to-blue-500 flex-shrink-0" />

            {/* Close button */}
            <button
              onClick={handleClose}
              className={`absolute top-4 right-4 z-10 p-1.5 rounded-lg transition-all duration-150 ${
                isDark
                  ? 'text-white/40 hover:text-white/80 hover:bg-white/10'
                  : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
              }`}
            >
              <X size={16} />
            </button>

            <div className="px-6 pt-6 pb-5 overflow-y-auto">
              <AnimatePresence mode="wait">
                {renderLimitView()}
              </AnimatePresence>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
};

export default AILimitModal;
